import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/useMobile";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { 
  GraduationCap, Users, Clock, MapPin, ChevronRight, ChevronDown, UserCheck, Mail, Phone,
  Calendar, CalendarPlus, ArrowLeft, CheckCircle, XCircle, AlertCircle, BarChart3, TrendingUp, ArchiveRestore, Ban,
  Dumbbell, Search, UserPlus
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CSSProperties, useState } from "react";
import { DashboardLayoutSkeleton } from "@/components/DashboardLayoutSkeleton";
import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { toast } from "sonner";
import { hasAnyRole } from "@/lib/roles";
import { CancelSessionDialog } from "@/components/CancelSessionDialog";
import { ReactivateSessionDialog } from "@/components/ReactivateSessionDialog";
import { trackSessionAttendanceMarked } from "@/lib/tracking";

const DAYS_DE = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

// Format date to German locale (short format for mobile: Mo. 19.12.25)
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("de-DE", { 
    weekday: "short", 
    day: "2-digit", 
    month: "2-digit",
    year: "2-digit"
  });
}

// Check if a date is in the past
function isPastDate(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const sessionDate = new Date(dateStr + "T00:00:00");
  return sessionDate < today;
}

// Check if a date is today
function isToday(dateStr: string): boolean {
  const today = new Date();
  const sessionDate = new Date(dateStr + "T00:00:00");
  return today.toDateString() === sessionDate.toDateString();
}

type ViewMode = "courses" | "sessions" | "attendance" | "gymSlots" | "gymSlotAttendance";
type ActiveTab = "courses" | "gym";

export default function InstructorDashboard() {
  const { user, loading: authLoading } = useAuth();
  const isMobile = useIsMobile();
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("courses");
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generateStartDate, setGenerateStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [generateEndDate, setGenerateEndDate] = useState(() => {
    const future = new Date();
    future.setMonth(future.getMonth() + 3);
    return future.toISOString().split('T')[0];
  });
  
  // Cancellation dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelSessionId, setCancelSessionId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  
  // Reactivation dialog state
  const [showReactivateDialog, setShowReactivateDialog] = useState(false);
  const [reactivateSessionId, setReactivateSessionId] = useState<number | null>(null);
  const [sendReactivateEmail, setSendReactivateEmail] = useState(true);
  
  // Collapsible past sessions state
  const [showPastSessions, setShowPastSessions] = useState(false);
  
  // Tab state for courses vs gym
  const [activeTab, setActiveTab] = useState<ActiveTab>("courses");

  // Add participant search state
  const [participantSearch, setParticipantSearch] = useState("");
  
  // Gym slot state
  const [selectedGymSlotId, setSelectedGymSlotId] = useState<number | null>(null);

  const { data: courses, isLoading: coursesLoading } = trpc.instructor.myCourses.useQuery();
  const { data: blocks, isLoading: blocksLoading } = trpc.instructor.myBlocks.useQuery();
  const { data: todaysSessions, isLoading: todaysSessionsLoading, refetch: refetchTodaysSessions } = trpc.instructor.getTodaysSessions.useQuery();
  
  // Gym config and slots queries
  const { data: gymConfig } = trpc.gym.getConfig.useQuery();
  const { data: gymSlots, isLoading: gymSlotsLoading, refetch: refetchGymSlots } = trpc.gym.instructor.mySlots.useQuery(
    undefined,
    { enabled: gymConfig?.enabled === true }
  );
  const { data: todayGymSlots, isLoading: todayGymSlotsLoading, refetch: refetchTodayGymSlots } = trpc.gym.instructor.todaySlots.useQuery(
    undefined,
    { enabled: gymConfig?.enabled === true }
  );
  const { data: gymSlotBookings, isLoading: gymSlotBookingsLoading, refetch: refetchGymSlotBookings } = trpc.gym.instructor.slotBookings.useQuery(
    { slotId: selectedGymSlotId! },
    { enabled: selectedGymSlotId !== null && viewMode === "gymSlotAttendance" }
  );
  const { data: sessions, isLoading: sessionsLoading, refetch: refetchSessions } = trpc.instructor.getCourseSessions.useQuery(
    { courseId: selectedCourseId! },
    { enabled: selectedCourseId !== null && viewMode === "sessions" }
  );
  const { data: sessionAttendance, isLoading: attendanceLoading, refetch: refetchAttendance } = trpc.session.getWithAttendance.useQuery(
    { sessionId: selectedSessionId!, courseId: selectedCourseId! },
    { enabled: selectedSessionId !== null && selectedCourseId !== null && viewMode === "attendance" }
  );
  const { data: courseBookingsForSession, refetch: refetchCourseBookings } = trpc.session.getCourseBookingsForSession.useQuery(
    { sessionId: selectedSessionId!, courseId: selectedCourseId!, search: participantSearch || undefined },
    { enabled: selectedSessionId !== null && selectedCourseId !== null && viewMode === "attendance" && participantSearch.length >= 2 }
  );
  const { data: courseStats } = trpc.session.getAttendanceStats.useQuery(
    { courseId: selectedCourseId! },
    { enabled: selectedCourseId !== null }
  );
  const { data: detailedStats } = trpc.session.getDetailedStats.useQuery(
    { courseId: selectedCourseId! },
    { enabled: selectedCourseId !== null && viewMode === "sessions" }
  );

  const utils = trpc.useUtils();
  
  const generateSessionsMutation = trpc.session.generateForCourse.useMutation({
    onSuccess: (data) => {
      toast.success(`${data.count} Termine erstellt`);
      refetchSessions();
      setShowGenerateDialog(false);
    },
    onError: () => toast.error("Fehler beim Erstellen der Termine"),
  });

  const markAttendanceMutation = trpc.session.markAttendance.useMutation({
    onSuccess: () => {
      refetchAttendance();
      utils.session.getAttendanceStats.invalidate();
      toast.success("Anwesenheit aktualisiert");
    },
    onError: () => toast.error("Fehler beim Speichern der Anwesenheit"),
  });

  const addParticipantMutation = trpc.session.addParticipantToSession.useMutation({
    onSuccess: () => {
      toast.success("Teilnehmer hinzugefügt");
      refetchAttendance();
      refetchCourseBookings();
      setParticipantSearch("");
    },
    onError: (e) => toast.error("Fehler", { description: e.message }),
  });

  const cancelSessionMutation = trpc.session.update.useMutation({
    onSuccess: (data) => {
      refetchSessions();
      setShowCancelDialog(false);
      setCancelSessionId(null);
      setCancelReason("");
      if (data.emailsSent && data.emailsSent > 0) {
        toast.success(`Termin abgesagt. ${data.emailsSent} Benachrichtigung(en) gesendet.`);
      } else {
        toast.success("Termin abgesagt");
      }
    },
    onError: () => toast.error("Fehler beim Absagen des Termins"),
  });

  const reactivateSessionMutation = trpc.session.update.useMutation({
    onSuccess: (data) => {
      refetchSessions();
      refetchTodaysSessions();
      setShowReactivateDialog(false);
      setReactivateSessionId(null);
      if (data.emailsSent && data.emailsSent > 0) {
        toast.success(`Termin reaktiviert. ${data.emailsSent} Benachrichtigung(en) gesendet.`);
      } else {
        toast.success("Termin reaktiviert");
      }
    },
    onError: () => toast.error("Fehler beim Reaktivieren des Termins"),
  });

  // Gym check-in mutation
  const gymCheckInMutation = trpc.gym.instructor.checkInMember.useMutation({
    onSuccess: () => {
      refetchGymSlotBookings();
      toast.success("Check-in erfolgreich");
    },
    onError: () => toast.error("Fehler beim Check-in"),
  });

  if (authLoading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user || !hasAnyRole(user.roles, ["admin", "courseadmin", "instructor"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Zugriff verweigert</CardTitle>
            <CardDescription>
              Sie haben keine Berechtigung, diese Seite zu sehen.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const selectedCourse = courses?.find(c => c.id === selectedCourseId);
  const selectedSession = sessions?.find(s => s.id === selectedSessionId);

  // Handle course selection
  const handleSelectCourse = (courseId: number) => {
    setSelectedCourseId(courseId);
    setSelectedSessionId(null);
    setViewMode("sessions");
  };

  // Handle session selection
  const handleSelectSession = (sessionId: number) => {
    setSelectedSessionId(sessionId);
    setViewMode("attendance");
  };

  // Go back to previous view
  const handleBack = () => {
    if (viewMode === "attendance") {
      setSelectedSessionId(null);
      setViewMode("sessions");
    } else if (viewMode === "sessions") {
      setSelectedCourseId(null);
      setViewMode("courses");
    } else if (viewMode === "gymSlotAttendance") {
      setSelectedGymSlotId(null);
      setViewMode("gymSlots");
    } else if (viewMode === "gymSlots") {
      setViewMode("courses");
    }
  };

  // Handle gym slot selection
  const handleSelectGymSlot = (slotId: number) => {
    setSelectedGymSlotId(slotId);
    setViewMode("gymSlotAttendance");
  };

  // Get selected gym slot details
  const selectedGymSlot = gymSlots?.find(s => s.id === selectedGymSlotId);

  // Format gym slot time
  const formatGymSlotTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${start.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} - ${end.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr`;
  };

  // Format gym slot date
  const formatGymSlotDate = (startTime: string) => {
    const date = new Date(startTime);
    return date.toLocaleDateString("de-DE", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit"
    });
  };

  // Count attendance for a session
  const getSessionAttendanceCount = (sessionId: number) => {
    // This would need a separate query or be included in session data
    return null;
  };

  return (
    <SidebarProvider
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Meine Termine</span>
            </div>
          </div>
        )}

        <main className="flex-1 p-4 md:p-6 space-y-6">
          {/* Header with breadcrumb */}
          <div className="flex items-center gap-4">
            {viewMode !== "courses" && (
              <Button variant="ghost" size="icon" onClick={handleBack}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight">
                {viewMode === "courses" && "Meine Termine"}
                {viewMode === "sessions" && selectedCourse?.name}
                {viewMode === "attendance" && selectedSession && formatDate(selectedSession.sessionDate)}
                {viewMode === "gymSlots" && "Meine Gym-Slots"}
                {viewMode === "gymSlotAttendance" && selectedGymSlot && formatGymSlotDate(selectedGymSlot.startTime)}
              </h1>
              <p className="text-muted-foreground">
                {viewMode === "courses" && "Wählen Sie einen Kurs oder Gym-Slot, um die Anwesenheit zu verwalten."}
                {viewMode === "sessions" && "Wählen Sie einen Termin, um die Anwesenheit zu erfassen."}
                {viewMode === "attendance" && `Anwesenheit für ${selectedCourse?.name}`}
                {viewMode === "gymSlots" && "Wählen Sie einen Slot, um die Teilnehmer zu verwalten."}
                {viewMode === "gymSlotAttendance" && selectedGymSlot && `Check-in für ${selectedGymSlot.service?.name || "Gym-Slot"}`}
              </p>
            </div>
          </div>

          {/* Tabs for Courses vs Gym (only show on main view) */}
          {viewMode === "courses" && gymConfig?.enabled && (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ActiveTab)} className="w-full">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="courses" className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  Kurse
                </TabsTrigger>
                <TabsTrigger value="gym" className="flex items-center gap-2">
                  <Dumbbell className="h-4 w-4" />
                  Gym-Slots
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Today's Sessions Shortcut (Courses) */}
          {viewMode === "courses" && activeTab === "courses" && todaysSessions && todaysSessions.length > 0 && (
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Calendar className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Heutige Termine</CardTitle>
                    <CardDescription>Schnellzugriff auf die Anwesenheitserfassung</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {todaysSessions.map(({ session, course, attendanceStats }) => (
                    <div 
                      key={session.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background border cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => {
                        setSelectedCourseId(course.id);
                        setSelectedSessionId(session.id);
                        setViewMode("attendance");
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                          <UserCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="font-medium">{course.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {course.startTime} - {course.endTime} Uhr
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2 text-sm">
                            {attendanceStats.unmarked > 0 ? (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                {attendanceStats.unmarked} offen
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Vollständig
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {attendanceStats.present}/{attendanceStats.total} anwesend
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Today's Gym Slots Shortcut */}
          {viewMode === "courses" && activeTab === "gym" && todayGymSlots && todayGymSlots.length > 0 && (
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-full bg-primary/10">
                    <Dumbbell className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Heutige Gym-Slots</CardTitle>
                    <CardDescription>Schnellzugriff auf Check-in</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  {todayGymSlots.map((slot) => {
                    const checkedInCount = slot.bookingCount; // We'd need to track checked-in separately
                    return (
                      <div 
                        key={slot.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-background border cursor-pointer hover:border-primary/50 transition-colors"
                        onClick={() => handleSelectGymSlot(slot.id)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
                            <UserCheck className="h-4 w-4 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <p className="font-medium">{slot.service?.name || "Gym-Slot"}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatGymSlotTime(slot.startTime, slot.endTime)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Users className="h-3 w-3 mr-1" />
                              {slot.bookingCount}/{slot.maxParticipants}
                            </Badge>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Course List View */}
          {viewMode === "courses" && activeTab === "courses" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {coursesLoading ? (
                [1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))
              ) : courses?.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="pt-6 text-center text-muted-foreground">
                    <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Kurse zugewiesen</p>
                    <p className="text-sm mt-1">
                      Kontaktieren Sie den Administrator, um Kurse zugewiesen zu bekommen.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                courses?.map(course => (
                  <Card 
                    key={course.id}
                    className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
                    onClick={() => handleSelectCourse(course.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <h3 className="font-medium">{course.name}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            {DAYS_DE[course.dayOfWeek]}, {course.startTime} - {course.endTime}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Badge 
                          variant={course.bookedCount >= course.capacity ? "default" : "secondary"} 
                          className={`text-xs ${course.bookedCount >= course.capacity ? 'bg-green-600' : ''}`}
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {course.bookedCount}/{course.capacity} Plätze
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          <Calendar className="h-3 w-3 mr-1" />
                          Termine verwalten
                        </Badge>
                      </div>
                    </CardContent>
                   </Card>
                ))
              )}
              
              {/* Block Courses Section */}
              {blocks && blocks.length > 0 && (
                <>
                  <div className="col-span-full mt-6">
                    <h2 className="text-lg font-semibold mb-4">Meine Blockkurse</h2>
                  </div>
                  {blocks.map(block => (
                    <Card 
                      key={block.id}
                      className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
                      onClick={() => handleSelectCourse(block.courseId)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <h3 className="font-medium">{block.courseName}</h3>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="h-3.5 w-3.5" />
                              Block ab {new Date(block.startDate).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-3.5 w-3.5" />
                              {block.courseDay !== null && DAYS_DE[block.courseDay]}, {block.courseStartTime} - {block.courseEndTime}
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Badge 
                            variant={block.currentParticipants >= block.maxParticipants ? "default" : "secondary"} 
                            className={`text-xs ${block.currentParticipants >= block.maxParticipants ? 'bg-green-600' : ''}`}
                          >
                            <Users className="h-3 w-3 mr-1" />
                            {block.currentParticipants}/{block.maxParticipants} Plätze
                          </Badge>
                          <Badge 
                            variant={block.status === "active" ? "default" : "outline"}
                            className="text-xs"
                          >
                            {block.status === "upcoming" && "Bevorstehend"}
                            {block.status === "active" && "Aktiv"}
                            {block.status === "completed" && "Abgeschlossen"}
                            {block.status === "cancelled" && "Abgesagt"}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Gym Slots List View */}
          {viewMode === "courses" && activeTab === "gym" && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {gymSlotsLoading ? (
                [1, 2, 3].map(i => (
                  <Skeleton key={i} className="h-32 w-full" />
                ))
              ) : !gymSlots || gymSlots.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="pt-6 text-center text-muted-foreground">
                    <Dumbbell className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Keine Gym-Slots zugewiesen</p>
                    <p className="text-sm mt-1">
                      Kontaktieren Sie den Administrator, um Gym-Slots zugewiesen zu bekommen.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                gymSlots.map(slot => (
                  <Card 
                    key={slot.id}
                    className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
                    onClick={() => handleSelectGymSlot(slot.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <h3 className="font-medium">{slot.service?.name || "Gym-Slot"}</h3>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatGymSlotDate(slot.startTime)}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-3.5 w-3.5" />
                            {formatGymSlotTime(slot.startTime, slot.endTime)}
                          </div>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Badge 
                          variant={slot.bookingCount >= slot.maxParticipants ? "default" : "secondary"} 
                          className={`text-xs ${slot.bookingCount >= slot.maxParticipants ? 'bg-green-600' : ''}`}
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {slot.bookingCount}/{slot.maxParticipants} Plätze
                        </Badge>
                        {slot.service?.color && (
                          <Badge 
                            variant="outline" 
                            className="text-xs"
                            style={{ borderColor: slot.service.color, color: slot.service.color }}
                          >
                            {slot.service.name}
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Gym Slot Attendance View */}
          {viewMode === "gymSlotAttendance" && selectedGymSlotId && selectedGymSlot && (
            <div className="space-y-4">
              {/* Slot Info */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium">{selectedGymSlot.service?.name || "Gym-Slot"}</h3>
                      <p className="text-sm text-muted-foreground">
                        {formatGymSlotDate(selectedGymSlot.startTime)} • {formatGymSlotTime(selectedGymSlot.startTime, selectedGymSlot.endTime)}
                      </p>
                    </div>
                    <Badge variant="secondary">
                      <Users className="h-3 w-3 mr-1" />
                      {selectedGymSlot.bookingCount}/{selectedGymSlot.maxParticipants} Teilnehmer
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Bookings List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Teilnehmer</CardTitle>
                  <CardDescription>Check-in für diesen Slot verwalten</CardDescription>
                </CardHeader>
                <CardContent>
                  {gymSlotBookingsLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-12 w-full" />
                      ))}
                    </div>
                  ) : !gymSlotBookings || gymSlotBookings.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Keine Buchungen für diesen Slot</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>E-Mail</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Aktion</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {gymSlotBookings.map(booking => (
                          <TableRow key={booking.id}>
                            <TableCell className="font-medium">
                              {booking.user?.name || "Unbekannt"}
                            </TableCell>
                            <TableCell className="text-muted-foreground">
                              {booking.user?.email || "-"}
                            </TableCell>
                            <TableCell>
                              {booking.checkedInAt ? (
                                <Badge variant="default" className="bg-green-600">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Eingecheckt
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  Offen
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {!booking.checkedInAt && (
                                <Button
                                  size="sm"
                                  onClick={() => gymCheckInMutation.mutate({
                                    bookingId: booking.id,
                                    slotId: selectedGymSlotId
                                  })}
                                  disabled={gymCheckInMutation.isPending}
                                >
                                  <UserCheck className="h-4 w-4 mr-1" />
                                  Check-in
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Session List View */}
          {viewMode === "sessions" && selectedCourseId && (
            <div className="space-y-4">
              {/* Stats and Actions */}
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex gap-2">
                  {courseStats && (
                    <>
                      <Badge variant="secondary">
                        <Calendar className="h-3 w-3 mr-1" />
                        {courseStats.totalSessions} Termine
                      </Badge>
                      {courseStats.totalAttendanceRecords > 0 && (
                        <Badge variant="default" className="bg-green-600">
                          <UserCheck className="h-3 w-3 mr-1" />
                          {courseStats.attendanceRate}% Anwesenheit
                        </Badge>
                      )}
                    </>
                  )}
                </div>
                <Button onClick={() => setShowGenerateDialog(true)}>
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Termine generieren
                </Button>
              </div>

              {/* Attendance Statistics Chart */}
              {detailedStats && detailedStats.sessions.length > 0 && detailedStats.overall.totalPresent + detailedStats.overall.totalAbsent > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-primary" />
                        <CardTitle className="text-base">Anwesenheitsstatistik</CardTitle>
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <span className="font-medium text-green-600">{detailedStats.overall.averageAttendance}%</span>
                        <span className="text-muted-foreground">Durchschnitt</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Simple bar chart */}
                    <div className="space-y-3">
                      {/* Chart bars */}
                      <div className="flex items-end gap-1 h-32 border-b border-l pl-1 pb-1">
                        {detailedStats.sessions
                          .filter(s => !s.isCancelled)
                          .slice(-12) // Show last 12 sessions
                          .map((session, idx) => {
                            const hasData = session.presentCount + session.absentCount > 0;
                            return (
                              <div 
                                key={session.id} 
                                className="flex-1 flex flex-col items-center gap-0.5 group relative"
                              >
                                {/* Tooltip */}
                                <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                                  <div className="bg-popover text-popover-foreground text-xs rounded-md shadow-lg p-2 whitespace-nowrap border">
                                    <p className="font-medium">{formatDate(session.date)}</p>
                                    <p className="text-green-600">{session.presentCount} anwesend</p>
                                    <p className="text-red-500">{session.absentCount} abwesend</p>
                                    <p className="text-muted-foreground">{session.attendanceRate}%</p>
                                  </div>
                                </div>
                                {/* Bar */}
                                <div 
                                  className={`w-full rounded-t transition-all ${
                                    hasData 
                                      ? session.attendanceRate >= 80 
                                        ? 'bg-green-500' 
                                        : session.attendanceRate >= 50 
                                          ? 'bg-yellow-500' 
                                          : 'bg-red-500'
                                      : 'bg-muted'
                                  }`}
                                  style={{ 
                                    height: hasData ? `${Math.max(session.attendanceRate, 5)}%` : '5%',
                                    minHeight: '4px'
                                  }}
                                />
                              </div>
                            );
                          })}
                      </div>
                      {/* Legend */}
                      <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-green-500" />
                          <span>≥80%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-yellow-500" />
                          <span>50-79%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 rounded bg-red-500" />
                          <span>&lt;50%</span>
                        </div>
                      </div>
                      {/* Summary stats */}
                      <div className="grid grid-cols-3 gap-4 pt-2 border-t">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{detailedStats.overall.totalPresent}</p>
                          <p className="text-xs text-muted-foreground">Anwesend</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-red-500">{detailedStats.overall.totalAbsent}</p>
                          <p className="text-xs text-muted-foreground">Abwesend</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold">{detailedStats.overall.totalSessions}</p>
                          <p className="text-xs text-muted-foreground">Termine</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Sessions Table */}
              <Card>
                <CardContent className="p-0">
                  {sessionsLoading ? (
                    <div className="p-6 space-y-3">
                      {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-12 w-full" />
                      ))}
                    </div>
                  ) : sessions?.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                      <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Noch keine Termine für diesen Kurs</p>
                      <p className="text-sm mt-1">
                        Klicken Sie auf "Termine generieren", um Termine zu erstellen.
                      </p>
                    </div>
                  ) : (() => {
                    const upcomingSessions = sessions?.filter(s => !isPastDate(s.sessionDate)) || [];
                    const pastSessions = sessions?.filter(s => isPastDate(s.sessionDate)) || [];
                    
                    const renderSessionCard = (session: NonNullable<typeof sessions>[number]) => (
                      <div 
                        key={session.id}
                        className={`p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors ${session.isCancelled ? 'opacity-60' : ''}`}
                      >
                        {/* Row 1: Date and status badges */}
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {isToday(session.sessionDate) ? (
                              <Badge variant="default" className="bg-blue-600 shrink-0">Heute</Badge>
                            ) : isPastDate(session.sessionDate) ? (
                              <Badge variant="secondary" className="shrink-0">Vergangen</Badge>
                            ) : (
                              <Badge variant="outline" className="shrink-0">Geplant</Badge>
                            )}
                            <span className="font-medium">{formatDate(session.sessionDate)}</span>
                          </div>
                          {session.isCancelled ? (
                            <Badge variant="destructive" className="shrink-0">
                              <XCircle className="h-3 w-3 mr-1" />
                              Abgesagt
                            </Badge>
                          ) : (
                            <Badge variant="default" className="bg-green-600 shrink-0">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Aktiv
                            </Badge>
                          )}
                        </div>
                        {/* Row 2: Action buttons */}
                        <div className="flex items-center justify-end gap-2 pt-2 border-t">
                          {!session.isCancelled && !isPastDate(session.sessionDate) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-amber-600 hover:text-amber-600 hover:bg-amber-600/10"
                              onClick={() => {
                                setCancelSessionId(session.id);
                                setShowCancelDialog(true);
                              }}
                            >
                              <XCircle className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">Absagen</span>
                            </Button>
                          )}
                          {!!session.isCancelled && !isPastDate(session.sessionDate) && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => {
                                setReactivateSessionId(session.id);
                                setShowReactivateDialog(true);
                              }}
                            >
                              <ArchiveRestore className="h-4 w-4" />
                              <span className="hidden sm:inline ml-1">Reaktivieren</span>
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSelectSession(session.id)}
                            disabled={session.isCancelled}
                          >
                            <UserCheck className="h-4 w-4" />
                            <span className="hidden sm:inline ml-1">Anwesenheit</span>
                          </Button>
                        </div>
                      </div>
                    );
                    
                    return (
                      <div className="p-4 space-y-2">
                        {/* Upcoming sessions */}
                        {upcomingSessions.map(renderSessionCard)}
                        
                        {/* Past sessions collapsible */}
                        {pastSessions.length > 0 && (
                          <div className="mt-4">
                            <button
                              onClick={() => setShowPastSessions(!showPastSessions)}
                              className="flex items-center gap-2 w-full p-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                            >
                              {showPastSessions ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                              <span>Vergangene Termine ({pastSessions.length})</span>
                            </button>
                            {showPastSessions && (
                              <div className="mt-2 space-y-2">
                                {pastSessions.map(renderSessionCard)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Attendance View */}
          {viewMode === "attendance" && selectedSessionId && selectedCourseId && (
            <div className="space-y-4">
              {/* Session Info */}
              {selectedSession && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Calendar className="h-8 w-8 text-primary" />
                        <div>
                          <p className="font-medium">{selectedCourse?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(selectedSession.sessionDate)} • {selectedCourse?.startTime} - {selectedCourse?.endTime}
                          </p>
                        </div>
                      </div>
                      {sessionAttendance && (
                        <div className="text-right">
                          <p className="text-2xl font-bold">
                            {sessionAttendance.attendance.filter(a => a.attended).length}/{sessionAttendance.attendance.length}
                          </p>
                          <p className="text-sm text-muted-foreground">Anwesend</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Attendance List - Mobile-friendly card layout */}
              <Card>
                <CardContent className="p-0">
                  {attendanceLoading ? (
                    <div className="p-4 space-y-3">
                      {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-20 w-full" />
                      ))}
                    </div>
                  ) : sessionAttendance?.attendance.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Keine Teilnehmer für diesen Kurs</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {sessionAttendance?.attendance.map(({ booking, attended }) => (
                        <div 
                          key={booking.id} 
                          className={`p-4 transition-colors ${
                            attended ? 'bg-green-50/50 dark:bg-green-950/20' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Checkbox */}
                            <div className="pt-0.5">
                              <Checkbox
                                checked={attended}
                                onCheckedChange={(checked) => 
                                  markAttendanceMutation.mutate({ 
                                    sessionId: selectedSessionId, 
                                    bookingId: booking.id, 
                                    attended: !!checked 
                                  })
                                }
                                disabled={markAttendanceMutation.isPending}
                                className="h-6 w-6"
                              />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {/* Name and Status Row */}
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="font-medium truncate">{booking.participantName}</span>
                                <Badge 
                                  variant={booking.status === "confirmed" ? "default" : "secondary"}
                                  className="shrink-0 text-xs"
                                >
                                  {booking.status === "confirmed" ? "Bestätigt" : "Ausstehend"}
                                </Badge>
                              </div>
                              
                              {/* Notes */}
                              {booking.notes && (
                                <p className="text-xs text-muted-foreground mb-2 italic">
                                  {booking.notes}
                                </p>
                              )}
                              
                              {/* Contact Info */}
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                <a 
                                  href={`mailto:${booking.participantEmail}`} 
                                  className="flex items-center gap-1.5 hover:text-primary transition-colors"
                                >
                                  <Mail className="h-3.5 w-3.5 shrink-0" />
                                  <span className="truncate">{booking.participantEmail}</span>
                                </a>
                                {booking.participantPhone && (
                                  <a 
                                    href={`tel:${booking.participantPhone}`} 
                                    className="flex items-center gap-1.5 hover:text-primary transition-colors"
                                  >
                                    <Phone className="h-3.5 w-3.5 shrink-0" />
                                    <span>{booking.participantPhone}</span>
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Add participant from course bookings */}
                  <div className="pt-4 border-t space-y-2">
                    <p className="text-sm font-medium px-4">Teilnehmer hinzufügen</p>
                    <div className="px-4">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Name oder E-Mail suchen..."
                          value={participantSearch}
                          onChange={(e) => setParticipantSearch(e.target.value)}
                          className="pl-8"
                        />
                      </div>
                    </div>
                    {participantSearch.length >= 2 && courseBookingsForSession && courseBookingsForSession.length > 0 && (
                      <div className="mx-4 border rounded-md divide-y max-h-48 overflow-y-auto">
                        {courseBookingsForSession.map((b) => (
                          <div key={b.id} className="flex items-center justify-between px-3 py-2 text-sm">
                            <div>
                              <span className="font-medium">{b.participantName}</span>
                              <span className="text-muted-foreground ml-2">{b.participantEmail}</span>
                            </div>
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => addParticipantMutation.mutate({ sessionId: selectedSessionId!, courseBookingId: b.id })}
                              disabled={addParticipantMutation.isPending}
                            >
                              <UserPlus className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {participantSearch.length >= 2 && courseBookingsForSession?.length === 0 && (
                      <p className="text-sm text-muted-foreground px-4">Keine Kursbuchungen gefunden.</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </SidebarInset>

      {/* Generate Sessions Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Termine generieren</DialogTitle>
            <DialogDescription>
              Erstellen Sie automatisch Termine für den Kurs "{selectedCourse?.name}" basierend auf dem Wochentag ({selectedCourse && DAYS_DE[selectedCourse.dayOfWeek]}).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="startDate">Startdatum</Label>
              <Input
                id="startDate"
                type="date"
                value={generateStartDate}
                onChange={(e) => setGenerateStartDate(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="endDate">Enddatum</Label>
              <Input
                id="endDate"
                type="date"
                value={generateEndDate}
                onChange={(e) => setGenerateEndDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => {
                if (selectedCourseId) {
                  generateSessionsMutation.mutate({
                    courseId: selectedCourseId,
                    startDate: generateStartDate,
                    endDate: generateEndDate,
                  });
                }
              }}
              disabled={generateSessionsMutation.isPending}
              className="bg-primary hover:bg-primary/90"
            >
              {generateSessionsMutation.isPending ? "Generiere..." : "Generieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Session Dialog */}
      <CancelSessionDialog
        open={showCancelDialog}
        onOpenChange={(open) => {
          setShowCancelDialog(open);
          if (!open) setCancelSessionId(null);
        }}
        onConfirm={({ reason, sendEmail }) => {
          if (cancelSessionId) {
            cancelSessionMutation.mutate({
              id: cancelSessionId,
              isCancelled: true,
              sendCancellationEmail: sendEmail,
              cancellationReason: reason,
            });
          }
        }}
        isPending={cancelSessionMutation.isPending}
      />

      {/* Reactivate Session Dialog */}
      <ReactivateSessionDialog
        open={showReactivateDialog}
        onOpenChange={(open) => {
          setShowReactivateDialog(open);
          if (!open) setReactivateSessionId(null);
        }}
        onConfirm={({ sendEmail }) => {
          if (reactivateSessionId) {
            reactivateSessionMutation.mutate({
              id: reactivateSessionId,
              isCancelled: false,
              sendReactivationEmail: sendEmail,
            });
          }
        }}
        isPending={reactivateSessionMutation.isPending}
      />
    </SidebarProvider>
  );
}
